import type {
  QuickContactCandidatesResponse,
  QuickContactSubmitRequest,
  QuickContactSubmitResponse,
  QuickContactTypedFieldsDto,
  VoterFuzzyCandidateDto,
  VoterSearchResultDto,
} from '@signalvote/shared';
import type { ApiClient } from '../client';
import { withQuery } from '../query';

export function createSearchVoters(client: ApiClient) {
  return (query: { q: string; lat?: number; lng?: number; limit?: number }, signal?: AbortSignal) =>
    client.request<VoterSearchResultDto[]>(withQuery('/api/voters/search', query), { method: 'GET', signal });
}

/**
 * ACS-005: Thrown by `submitCanvasserAdded` when the server's duplicate
 * detector returns a 409. Mobile clients catch this to reroute the
 * canvasser into the Log Contact flow for the existing voter, carrying
 * over their typed support and notes.
 *
 * The api-client surfaces this as a typed error rather than a generic
 * "Request failed" string so callers don't need to grep error messages
 * to distinguish duplicate from other failure modes.
 */
export class DuplicateVoterError extends Error {
  /** Id of the existing voter the server matched against. */
  readonly existingVoterId: string;

  constructor(existingVoterId: string) {
    super('Duplicate voter detected.');
    this.name = 'DuplicateVoterError';
    this.existingVoterId = existingVoterId;
    // Restore prototype chain — needed for `instanceof` to work after
    // transpilation to ES5 targets (some bundle configs in this monorepo).
    Object.setPrototypeOf(this, DuplicateVoterError.prototype);
  }
}

/** Type guard for `DuplicateVoterError`. Survives prototype-chain quirks
 *  across module / bundler boundaries by also checking the name marker. */
export function isDuplicateVoterError(value: unknown): value is DuplicateVoterError {
  if (value instanceof DuplicateVoterError) return true;
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { name?: string }).name === 'DuplicateVoterError' &&
    typeof (value as { existingVoterId?: unknown }).existingVoterId === 'string'
  );
}

/**
 * ACS-006: Thrown by `submitCanvasserAdded` when the server's duplicate
 * detector returned fuzzy candidates (no exact match). The server returns
 * 200 OK in this case (no row written, awaiting the canvasser's choice);
 * the api-client surfaces this as a typed error so callers route into the
 * "Did you mean?" screen instead of treating the 200 as a successful
 * create. The fuzzy path is structurally distinct from a 409 (exact match)
 * — different copy, different routing.
 */
export class FuzzyVoterCandidatesError extends Error {
  readonly candidates: VoterFuzzyCandidateDto[];
  readonly typedFields: QuickContactTypedFieldsDto;

  constructor(candidates: VoterFuzzyCandidateDto[], typedFields: QuickContactTypedFieldsDto) {
    super('Fuzzy voter candidates returned.');
    this.name = 'FuzzyVoterCandidatesError';
    this.candidates = candidates;
    this.typedFields = typedFields;
    Object.setPrototypeOf(this, FuzzyVoterCandidatesError.prototype);
  }
}

/** Type guard for `FuzzyVoterCandidatesError`. Same prototype-chain hedge
 *  as `isDuplicateVoterError` for cross-bundle reliability. */
export function isFuzzyVoterCandidatesError(
  value: unknown,
): value is FuzzyVoterCandidatesError {
  if (value instanceof FuzzyVoterCandidatesError) return true;
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { name?: string }).name === 'FuzzyVoterCandidatesError' &&
    Array.isArray((value as { candidates?: unknown }).candidates)
  );
}

export function createSubmitCanvasserAdded(client: ApiClient) {
  // QC-008: Quick Contact submit — server applies the classification rule
  // and returns { id, classification }. The classification drives which
  // alert the client renders.
  //
  // ACS-005: A 409 Conflict response means the duplicate detector found
  // an exact match; throw a typed DuplicateVoterError carrying the
  // existing voter id so the mobile client can reroute. The non-409
  // failure path stays unchanged (generic Error).
  //
  // ACS-006: A 200 OK response with `{ candidates, typedFields }` means
  // the detector returned fuzzy candidates (no exact match) — no row was
  // written. Throw a typed FuzzyVoterCandidatesError so callers route
  // into the "Did you mean?" screen. The 201 Created success path is
  // unchanged (id + classification only).
  return async (request: QuickContactSubmitRequest): Promise<QuickContactSubmitResponse> => {
    const response = await client.requestRaw('/api/voters/canvasser-added', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.status === 409) {
      const body = (await response.json().catch(() => ({}))) as {
        existingVoterId?: string;
      };
      if (typeof body.existingVoterId === 'string' && body.existingVoterId.length > 0) {
        throw new DuplicateVoterError(body.existingVoterId);
      }
      // Defensive: a 409 without the expected body is a server-shape
      // mismatch — fall through to a generic error so the alert path
      // still tells the canvasser something went wrong.
      throw new Error('Conflict: existing voter found, but server response was malformed.');
    }

    if (!response.ok) {
      const error = (await response.json().catch(() => ({ message: 'Request failed' }))) as {
        message?: string;
        error?: string;
      };
      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    // 200 vs 201 disambiguation: the server returns 200 only for the
    // ACS-006 fuzzy-candidates response; 201 is the create-success
    // shape. Parse once and branch on the body shape rather than the
    // status code so a hypothetical proxy that rewrites 201 → 200 still
    // routes correctly.
    const body = (await response.json()) as QuickContactSubmitResponse | QuickContactCandidatesResponse;

    if ('candidates' in body && Array.isArray(body.candidates)) {
      throw new FuzzyVoterCandidatesError(body.candidates, body.typedFields);
    }

    return body as QuickContactSubmitResponse;
  };
}
